import React, { useState } from 'react';
import {
  Bug,
  HelpCircle,
  Lightbulb,
  Mail,
  MessageSquare,
  Search,
  UserCircle,
} from 'lucide-react';

// Import components
import SeoHead from '../components/seo/SeoHead';
import JsonLd from '../components/seo/JsonLd';
import { getSupportPageJsonLd } from '../components/seo/structuredData';
import StickyNavbar from '../components/navbar/StickyNavbar';
import SupportModal from '../components/support/SupportModal';
import DisplayAd from '../components/ads/DisplayAd';
import Footer from '../components/landing/Footer';

type SupportBlock = {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  badgeClass: string;
  iconClass: string;
};

const supportBlocks: SupportBlock[] = [
  {
    id: 'contact',
    icon: Mail,
    title: 'Contact Us',
    description: 'Get in touch with our team for any questions or inquiries',
    badgeClass: 'bg-blue-50',
    iconClass: 'text-blue-600',
  },
  {
    id: 'feedback',
    icon: MessageSquare,
    title: 'Feedback',
    description: 'Share your thoughts and suggestions to help us improve',
    badgeClass: 'bg-green-50',
    iconClass: 'text-green-700',
  },
  {
    id: 'bug',
    icon: Bug,
    title: 'Bug Report',
    description: 'Report any issues or bugs you encounter while using the app',
    badgeClass: 'bg-red-50',
    iconClass: 'text-red-600',
  },
  {
    id: 'feature',
    icon: Lightbulb,
    title: 'Feature Request',
    description: 'Suggest new features or improvements you would like to see',
    badgeClass: 'bg-yellow-50',
    iconClass: 'text-yellow-600',
  },
  {
    id: 'user',
    icon: UserCircle,
    title: 'User Help',
    description: 'Get assistance with user-related questions or issues',
    badgeClass: 'bg-purple-50',
    iconClass: 'text-purple-600',
  },
  {
    id: 'general',
    icon: HelpCircle,
    title: 'General Inquiry',
    description: 'Ask any other questions or seek general information',
    badgeClass: 'bg-gray-50',
    iconClass: 'text-gray-700',
  },
];

export default function Support() {
  const [selectedBlock, setSelectedBlock] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleBlockClick = (blockId: string) => {
    setSelectedBlock(blockId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBlock(null);
  };

  const selectedBlockData = supportBlocks.find((b) => b.id === selectedBlock);

  return (
    <div className="w-full min-h-screen bg-white text-gray-800 flex flex-col">
      <SeoHead route="/support" canonicalPath="/support" />
      <JsonLd data={getSupportPageJsonLd()} />
      <StickyNavbar />

      <main className="flex flex-col py-20 md:py-24 px-4 max-w-6xl mx-auto w-full">
        <div className="flex flex-col items-center gap-8">
          {/* Header */}
          <div className="flex flex-col items-center gap-6 w-full">
            <h1 className="text-4xl sm:text-5xl font-bold text-center">
              Hi, how can we help you?
            </h1>

            {/* Search Bar */}
            <div className="w-full max-w-md hidden">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent"
                />
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <Search className="w-5 h-5 text-black" />
                </div>
              </div>
            </div>
          </div>

          {/* Support Blocks Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
            {supportBlocks.map((block) => {
              const IconComponent = block.icon;
              return (
                <button
                  key={block.id}
                  onClick={() => handleBlockClick(block.id)}
                  className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer text-left flex flex-col items-center gap-4 hover:border-gray-300">
                  <div
                    className={`flex h-14 w-14 items-center justify-center rounded-full ${block.badgeClass}`}>
                    <IconComponent className={`w-8 h-8 ${block.iconClass}`} />
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <h3 className="text-lg font-semibold text-black">
                      {block.title}
                    </h3>
                    <p className="text-base text-gray-600 text-center">
                      {block.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mx-auto">
          <DisplayAd />
        </div>
      </main>

      <Footer />

      {/* Modal */}
      <SupportModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={selectedBlockData?.title || 'Support'}
      />
    </div>
  );
}
